(() => {
  const headerSelector = '[data-ibh-header]';
  const drawerSelector = '[data-header-drawer]';
  const drawerToggleSelector = '[data-header-drawer-toggle]';
  const searchSelector = '[data-header-search]';
  const searchToggleSelector = '[data-header-search-toggle]';
  const searchFormSelector = '[data-header-search-form]';
  const searchInputSelector = '[data-header-search-input]';
  const searchResultsSelector = '[data-header-search-results]';

  const wildcardSearchTerm = (value) => {
    const query = value.trim();

    if (!query || query.endsWith('*') || /[:"]/u.test(query)) return query;

    return `${query}*`;
  };

  const submitSearchForm = (form, input) => {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    const action = form.getAttribute('action') || window.location.pathname;

    params.set(input.name, wildcardSearchTerm(input.value));
    window.location.href = `${action}?${params.toString()}`;
  };

  const updateStickyOffset = () => {
    const announcementBar = document.querySelector(
      '[data-announcement-bar][data-sticky-behavior="sticky"], [data-announcement-bar][data-sticky-behavior="hide_on_scroll"]'
    );
    const offset = announcementBar ? announcementBar.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--announcement-bar-sticky-offset', `${offset}px`);
  };

  const initStickyHeader = (header) => {
    if (header.dataset.stickyInitialized === 'true') return;

    header.dataset.stickyInitialized = 'true';
    updateStickyOffset();
    window.addEventListener('resize', updateStickyOffset, { passive: true });

    if (header.dataset.stickyBehavior !== 'hide_on_scroll') return;

    let lastScrollY = window.scrollY;
    let ticking = false;
    const threshold = 96;

    const updateHeader = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY;

      if (currentScrollY <= 0) {
        header.classList.remove('is-header-hidden');
      } else if (scrollingDown && currentScrollY > threshold) {
        header.classList.add('is-header-hidden');
      } else if (!scrollingDown) {
        header.classList.remove('is-header-hidden');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;

        window.requestAnimationFrame(updateHeader);
        ticking = true;
      },
      { passive: true }
    );
  };

  const setDrawerState = (header, open) => {
    const drawer = header.querySelector(drawerSelector);
    const toggle = header.querySelector(drawerToggleSelector);

    if (!drawer || !toggle) return;

    drawer.hidden = !open;
    header.classList.toggle('is-drawer-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  };

  const setSearchState = (search, open, focus = false) => {
    const form = search.querySelector(searchFormSelector);
    const toggle = search.querySelector(searchToggleSelector);
    const input = search.querySelector(searchInputSelector);
    const results = search.querySelector(searchResultsSelector);

    search.classList.toggle('is-search-open', open);

    if (form) form.hidden = !open;
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
    if (!open && results) results.hidden = true;
    if (open && focus && input) input.focus();
  };

  const closeSearches = (header, exceptSearch = null) => {
    header.querySelectorAll(searchSelector).forEach((search) => {
      if (search === exceptSearch) return;

      setSearchState(search, false);
    });
  };

  const initDrawer = (header) => {
    const toggle = header.querySelector(drawerToggleSelector);
    const drawer = header.querySelector(drawerSelector);

    if (!toggle || !drawer || toggle.dataset.drawerInitialized === 'true') return;

    toggle.dataset.drawerInitialized = 'true';

    toggle.addEventListener('click', () => {
      const willOpen = drawer.hidden;

      closeSearches(header);
      setDrawerState(header, willOpen);
    });

    drawer.addEventListener('click', (event) => {
      if (event.target.closest('a')) setDrawerState(header, false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || drawer.hidden) return;

      setDrawerState(header, false);
      toggle.focus();
    });

    document.addEventListener('click', (event) => {
      if (drawer.hidden || header.contains(event.target)) return;
      setDrawerState(header, false);
    });
  };

  const initSearch = (header) => {
    header.querySelectorAll(searchSelector).forEach((search) => {
      if (search.dataset.searchInitialized === 'true') return;

      search.dataset.searchInitialized = 'true';

      const toggle = search.querySelector(searchToggleSelector);
      const form = search.querySelector(searchFormSelector);
      const input = search.querySelector(searchInputSelector);
      const results = search.querySelector(searchResultsSelector);
      const types = search.dataset.searchTypes || 'product';
      let timeout = null;
      let abortController = null;

      setSearchState(search, false);

      if (toggle && form) {
        toggle.addEventListener('click', () => {
          const willOpen = !search.classList.contains('is-search-open');

          closeSearches(header, search);
          setDrawerState(header, false);
          setSearchState(search, willOpen, willOpen);
        });
      }

      if (form && input) {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          submitSearchForm(form, input);
        });
      }

      if (!input || !results) return;

      input.addEventListener('input', () => {
        const query = input.value.trim();

        window.clearTimeout(timeout);

        if (query.length < 2) {
          results.hidden = true;
          results.replaceChildren();
          return;
        }

        timeout = window.setTimeout(() => {
          const params = new URLSearchParams();
          params.set('q', query);
          params.set('section_id', 'predictive-search');
          params.set('resources[type]', types);
          params.set('resources[limit]', '4');
          params.set('resources[options][unavailable_products]', 'last');

          if (abortController) abortController.abort();
          abortController = new AbortController();

          fetch(`/search/suggest?${params.toString()}`, {
            headers: { Accept: 'text/html' },
            signal: abortController.signal
          })
            .then((response) => (response.ok ? response.text() : null))
            .then((html) => {
              if (!html) {
                results.hidden = true;
                return;
              }

              const doc = new DOMParser().parseFromString(html, 'text/html');
              const section = doc.querySelector('#shopify-section-predictive-search');
              const markup = section ? section.innerHTML : html;

              results.innerHTML = markup;
              results.hidden = !results.textContent.trim();
            })
            .catch((error) => {
              if (error.name !== 'AbortError') results.hidden = true;
            });
        }, 180);
      });

      document.addEventListener('click', (event) => {
        if (search.contains(event.target)) return;
        results.hidden = true;
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        results.hidden = true;

        if (toggle && form) {
          setSearchState(search, false);
          toggle.focus();
        }
      });
    });
  };

  const initHeader = (scope = document) => {
    scope.querySelectorAll(headerSelector).forEach((header) => {
      initStickyHeader(header);
      initDrawer(header);
      initSearch(header);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHeader());
  } else {
    initHeader();
  }

  document.addEventListener('shopify:section:load', (event) => initHeader(event.target));
})();
